import { Swiper as SwiperType } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ImageTextPaginated from '../../components/shared/ImageTextPaginated/ImageTextPaginated';
import ReEnergizePanel from '../../components/HorizontalCarousel/panels/ReEnergizePanel';
import HorizontalCarousel from '../../components/HorizontalCarousel/HorizontalCarousel';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Hero from '../../components/Hero/Hero';
import Footer from '../../components/Footer/Footer';

import { images } from '../../components/shared/assets';
import { createSelfbookHandler } from '../../utils/selfbook';
import { PageToggleButton } from '../../components/shared/PageToggleButton/PageToggleButton';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';

import './ReEnergizePage.css';
import 'swiper/css';

interface WellnessCard {
  id: string;
  title: string;
  location: string;
  category: string;
  description: string;
  image?: string;
}

const ReEnergizePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const handleBooking = createSelfbookHandler(navigate);

  const wellnessCards: WellnessCard[] = [
    {
      id: '1',
      title: t('pages/reEnergize:cards.atacamaRetreat.title'),
      location: t('pages/reEnergize:cards.atacamaRetreat.location'),
      category: 'wellness',
      description: t('pages/reEnergize:cards.atacamaRetreat.description'),
      image: images.destinationsPage.properties.atacama
    },
    {
      id: '2',
      title: t('pages/reEnergize:cards.santaCatarinaRetreat.title'),
      location: t('pages/reEnergize:cards.santaCatarinaRetreat.location'),
      category: 'wellness',
      description: t('pages/reEnergize:cards.santaCatarinaRetreat.description'),
      image: images.destinationsPage.properties.santaCatarina
    },
    {
      id: '3',
      title: t('pages/reEnergize:cards.mendozaSpa.title'),
      location: t('pages/reEnergize:cards.mendozaSpa.location'),
      category: 'wellness',
      description: t('pages/reEnergize:cards.mendozaSpa.description'),
      image: images.destinationsPage.properties.mendoza
    },
    {
      id: '4',
      title: 'Desert Mindfulness Retreat',
      location: 'awasi atacama',
      category: 'wellness',
      description: 'Experience the profound silence of the Atacama Desert through guided meditation sessions and mindfulness practices that connect you with the natural world.',
      image: images.destinationsPage.properties.atacama2
    },
    {
      id: '5',
      title: 'Mountain Yoga Sessions',
      location: 'awasi patagonia',
      category: 'wellness',
      description: 'Begin your day with gentle yoga overlooking the Torres del Paine, breathing in the crisp mountain air while strengthening body and mind.',
      image: images.destinationsPage.properties.patagonia
    }
  ];

  // Wellness Treatments Section Component
  const WellnessTreatments: React.FC = () => {
    return (
      <section className="wellness-treatments" data-scroll-section>
        <div className="wellness-treatments__content">
          <h2>{t('pages/reEnergize:treatments.title')}</h2>
          <div className="wellness-treatments__body">
            <p>
              {t('pages/reEnergize:treatments.description')}
            </p>
            <HandDrawnButton
              text={t('pages/reEnergize:treatments.button')}
              variant="brand"
              color="awasi"
              animation="stroke-to-fill"
              onClick={handleBooking}
              className="wellness-treatments__button"
            />
          </div>
        </div>
      </section>
    );
  };

  // Horizontal Gallery Carousel for wellness cards
  const WellnessGalleryCarousel: React.FC = () => {
    const swiperRef = useRef<SwiperType | null>(null);

    const showButtons = false;

    const handleArrowClick = (direction: 'left' | 'right') => {
      if (swiperRef.current) {
        if (direction === 'left') {
          swiperRef.current?.slidePrev();
        } else {
          swiperRef.current?.slideNext();
        }
      }
    };

    return (
      <section className="wellness-gallery-carousel" data-scroll-section>
        {/* Carousel Section */}
        <div className="glacier-video-exploration__carousel-container  wellness-gallery-carousel__scroll-container">
          <Swiper
            freeMode
            navigation
            spaceBetween={20}
            slidesPerView={1}
            className='glacier-video-exploration__carousel-swiper'
            breakpoints={{
              1024: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
            }}
            onSwiper={(currentSwiper: SwiperType) => swiperRef.current = currentSwiper}
          >
            {wellnessCards.map((card) => (
              <SwiperSlide key={card.id}>
                <div className="wellness-gallery-carousel__card">
                  <div className="wellness-gallery-carousel__image">
                    {card.image && (
                      <img src={card.image} alt={card.title} />
                    )}
                  </div>
                  <div className="wellness-gallery-carousel__content">
                    <div className="wellness-gallery-carousel__header">
                      <h3>{card.title}</h3>
                      <div className="wellness-gallery-carousel__meta">
                        <span>{card.location}</span>
                        <span>-</span>
                        <span>{card.category}</span>
                      </div>
                    </div>
                    <p>{card.description}</p>
                    {
                      showButtons &&
                      <CTALink
                        text={t('pages/reEnergize:cards.readMore')}
                        type="primary"
                        color="awasi"
                        onClick={() => navigate('/re-energize/treatments/')}
                        className="wellness-gallery-carousel__link"
                      />
                    }
                  </div>
                </div>
              </SwiperSlide>
            ))}

          </Swiper>
          <div className="glacier-video-exploration__arrow left" onClick={() => handleArrowClick('left')}>
            <img
              src={images.carouselArrow}
              alt="Scroll to see more destinations"
              className="glacier-video-exploration__arrow-image"
            />
          </div>

          <div className="glacier-video-exploration__arrow" onClick={() => handleArrowClick('right')}>
            <img
              src={images.carouselArrow}
              alt="Scroll to see more destinations"
              className="glacier-video-exploration__arrow-image"
            />
          </div>
        </div>
      </section>
    );
  };

  // Seasonal Produce section - Figma Design Implementation
  const SeasonalProduce: React.FC = () => {
    return (
      <section className="seasonal-produce" data-scroll-section>

        {/* Header Section */}
        <div className="seasonal-produce__header">
          <h2>{t('pages/reEnergize:seasonalProduce.title')}</h2>
        </div>

        {/* Image with Details Section */}
        <div className="seasonal-produce__image-container">
          <div
            className="seasonal-produce__background-image"
            style={{ backgroundImage: `url('/assets/857a4182e049ba5243880b0dcb008dfde8ecce1c.png')` }}
          />

          {/* Details Box - Positioned overlay */}
          <div className="seasonal-produce__details-box">
            <div className="seasonal-produce__content">
              <h3>{t('pages/reEnergize:seasonalProduce.details.title')}</h3>
              <p>
                {t('pages/reEnergize:seasonalProduce.details.description')}
              </p>
              <CTALink
                text={t('pages/reEnergize:seasonalProduce.details.button')}
                type="primary"
                color="awasi"
                onClick={() => navigate('/re-energize/nutrition/')}
                className="seasonal-produce__link"
              />
            </div>

            {/* Page Navigation */}
            <div className="seasonal-produce__pagination">
              {[1, 2, 3, 4].map(pageNum => (
                <PageToggleButton
                  key={pageNum}
                  number={pageNum}
                  isActive={currentPage === pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  color="off-white"
                  className="seasonal-produce__page-btn"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="re-energize-page">


        <title>{t('meta:reEnergize.title')}</title>
        <meta name="description" content={t('meta:reEnergize.description')} />
        <meta property="og:title" content={t('meta:reEnergize.title')} />
        <meta property="og:description" content={t('meta:reEnergize.description')} />


      {/* Navigation - Transparent with white text/icons for hero */}
      <SmartNavigation
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
        forceWhiteAssets={true}
      />

      {/* Hero Section */}
      <Hero
        backgroundVideo = '/assets/re-energize-hero-video.mp4'
        heading={t('pages/reEnergize:hero.title')}
      />

      {/* Wellness Carousel Section */}
      <HorizontalCarousel>
        <ReEnergizePanel />
      </HorizontalCarousel>

      {/* Wellness Treatments Section */}
      <WellnessTreatments />

      {/* Horizontal Gallery Carousel */}
      <WellnessGalleryCarousel />

      {/* Seasonal Produce Section */}
      <div data-scroll-section="dining" className="scroll-reveal-section">
        <ImageTextPaginated
          backgroundColor="#49413D"
          foregroundColor="#F3F3F3"
          heading={t('pages/reEnergize:retreats.title')}
          slides={[
            {
              heading: t('pages/reEnergize:retreats.moments.title'),
              description: t('pages/reEnergize:retreats.moments.description'),
              imageUrl: "/assets/images/reenergize/reenergize-01.png"
            },
            {
              heading: t('pages/reEnergize:retreats.atacamaRetreat.title'),
              description: t('pages/reEnergize:retreats.atacamaRetreat.description'),
              imageUrl: "/assets/images/reenergize/reenergize-01.png"
            }
          ]}
        />

      </div>

      {/* Instagram Section */}
      <InstagramSection
        posts={[
          { src: images.film13, alt: "Awasi wellness" },
          { src: images.film14, alt: "Awasi nature" },
          { src: images.film15, alt: "Awasi experience" },
          { src: images.instagram4, alt: "Awasi luxury" }
        ]}
      />

      {/* Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default ReEnergizePage;